import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react'
import { Modal } from './Modal'
import { Button } from '../ui'

/**
 * In-App-Dialoge (H7 + Niedrig): ersetzen die nativen `prompt()/alert()/confirm()`,
 * die in einer installierten PWA/WebView unzuverlässig sind. Alle als Promise, damit
 * Aufrufer sie wie gewohnt `await`en können. Barrierearm über die Modal-Primitive.
 */
type ConfirmOpts = { title: string; message?: string; confirmLabel?: string; cancelLabel?: string; danger?: boolean }
type PromptOpts = { title: string; message?: string; defaultValue?: string; confirmLabel?: string; placeholder?: string }
type AlertOpts = { title: string; message?: string; okLabel?: string }
/** Auswahl aus mehreren Optionen (z. B. „Nur dieser / Dieser und folgende / Alle"). null = abgebrochen. */
export type ChooseOption = { value: string; label: string; hint?: string; danger?: boolean }
type ChooseOpts = { title: string; message?: string; options: ChooseOption[] }

type DialogApi = {
  confirm: (opts: ConfirmOpts) => Promise<boolean>
  prompt: (opts: PromptOpts) => Promise<string | null>
  alert: (opts: AlertOpts) => Promise<void>
  choose: (opts: ChooseOpts) => Promise<string | null>
}

const DialogContext = createContext<DialogApi | null>(null)

type State =
  | { kind: 'confirm'; opts: ConfirmOpts; resolve: (v: boolean) => void }
  | { kind: 'prompt'; opts: PromptOpts; resolve: (v: string | null) => void }
  | { kind: 'alert'; opts: AlertOpts; resolve: () => void }
  | { kind: 'choose'; opts: ChooseOpts; resolve: (v: string | null) => void }
  | null

export function DialogProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const [state, setState] = useState<State>(null)
  const [text, setText] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  const confirm = useCallback(
    (opts: ConfirmOpts) => new Promise<boolean>((resolve) => setState({ kind: 'confirm', opts, resolve })),
    [],
  )
  const prompt = useCallback(
    (opts: PromptOpts) =>
      new Promise<string | null>((resolve) => {
        setText(opts.defaultValue ?? '')
        setState({ kind: 'prompt', opts, resolve })
      }),
    [],
  )
  const alert = useCallback(
    (opts: AlertOpts) => new Promise<void>((resolve) => setState({ kind: 'alert', opts, resolve })),
    [],
  )

  const choose = useCallback(
    (opts: ChooseOpts) => new Promise<string | null>((resolve) => setState({ kind: 'choose', opts, resolve })),
    [],
  )

  const close = (): void => setState(null)

  const titleId = 'app-dialog-title'
  const title = state?.opts.title ?? ''
  const message = state?.opts.message

  return (
    <DialogContext.Provider value={{ confirm, prompt, alert, choose }}>
      {children}
      {state && (
        <Modal onClose={() => { if (state.kind === 'confirm') state.resolve(false); else if (state.kind === 'prompt' || state.kind === 'choose') state.resolve(null); else state.resolve(); close() }} labelledBy={titleId} className="max-w-sm">
          <h2 id={titleId} className="text-base font-semibold" style={{ color: 'var(--color-ink)' }}>{title}</h2>
          {message && <p className="mt-2 text-sm" style={{ color: 'var(--color-muted)' }}>{message}</p>}
          {state.kind === 'prompt' && (
            <input
              ref={inputRef}
              autoFocus
              value={text}
              placeholder={state.opts.placeholder}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { state.resolve(text.trim() ? text : null); close() }
              }}
              className="mt-3 w-full rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2"
              style={{ borderColor: 'var(--color-line)', color: 'var(--color-ink)', backgroundColor: 'var(--color-surface)' }}
            />
          )}
          {state.kind === 'choose' ? (
            <div className="mt-4 flex flex-col gap-2">
              {state.opts.options.map((o) => (
                <button
                  key={o.value}
                  type="button"
                  onClick={() => { state.resolve(o.value); close() }}
                  className="rounded-lg border px-3 py-2.5 text-left text-sm hover:brightness-95"
                  style={{ borderColor: o.danger ? 'var(--color-danger)' : 'var(--color-line)', backgroundColor: 'var(--color-surface)', color: o.danger ? 'var(--color-danger)' : 'var(--color-ink)' }}
                >
                  <span className="font-medium">{o.label}</span>
                  {o.hint && <span className="mt-0.5 block text-xs" style={{ color: 'var(--color-muted)' }}>{o.hint}</span>}
                </button>
              ))}
              <Button variant="ghost" onClick={() => { state.resolve(null); close() }}>Abbrechen</Button>
            </div>
          ) : (
          <div className="mt-5 flex justify-end gap-2">
            {state.kind !== 'alert' && (
              <Button variant="ghost" onClick={() => { if (state.kind === 'confirm') state.resolve(false); else state.resolve(null); close() }}>
                {state.kind === 'confirm' ? (state.opts.cancelLabel ?? 'Abbrechen') : 'Abbrechen'}
              </Button>
            )}
            <Button
              variant={state.kind === 'confirm' && state.opts.danger ? 'danger' : 'primary'}
              onClick={() => {
                if (state.kind === 'confirm') state.resolve(true)
                else if (state.kind === 'prompt') state.resolve(text.trim() ? text : null)
                else state.resolve()
                close()
              }}
            >
              {state.kind === 'confirm' ? (state.opts.confirmLabel ?? 'OK') : state.kind === 'prompt' ? (state.opts.confirmLabel ?? 'OK') : (state.opts.okLabel ?? 'OK')}
            </Button>
          </div>
          )}
        </Modal>
      )}
    </DialogContext.Provider>
  )
}

export function useDialogs(): DialogApi {
  const ctx = useContext(DialogContext)
  if (!ctx) throw new Error('useDialogs muss innerhalb von DialogProvider verwendet werden')
  return ctx
}
